import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { AlarmService } from '../../_core/alarms';
import { Spinner } from '../../_core/spinner';
import { IAlarm } from '../../core.interface';

@Component({
  selector: 'app-alarms-historic',
  imports: [DatePipe, ButtonModule, DialogModule, PaginatorModule, TableModule],
  templateUrl: './alarms-historic.html',
  styleUrl: './alarms-historic.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlarmsHistoric {
  private $alarms = inject(AlarmService);
  private $spinner = inject(Spinner);

  alarmDeactivated = output<void>();

  visible = signal(false);
  alarmType = signal('');
  private typeId = signal<number | null>(null);
  private meterId = signal<string | null>(null);
  currentPage = signal(0);

  historicAlarms = this.$alarms.historicAlarms;
  historicTotalElements = this.$alarms.historicTotalElements;
  historicPageSize = this.$alarms.historicPageSize;

  firstAlarmActive = computed(() => {
    const first = this.historicAlarms()[0];
    return !!first && !first.expiredAt;
  });

  async open(alarm: IAlarm): Promise<void> {
    this.alarmType.set(alarm.type.description);
    this.typeId.set(alarm.type.id);
    this.meterId.set(alarm.meterId);
    this.currentPage.set(0);
    this.visible.set(true);
    this.$spinner.show();
    await this.$alarms.fetchAlarmByType(alarm.type.id, 0);
    this.$spinner.hide();
  }

  onVisibleChange(value: boolean): void {
    this.visible.set(value);
  }

  async onPageChange(event: PaginatorState): Promise<void> {
    const typeId = this.typeId();
    if (typeId === null) return;

    const page = event.page ?? 0;
    this.currentPage.set(page);
    this.$spinner.show();
    await this.$alarms.fetchAlarmByType(typeId, page);
    this.$spinner.hide();
  }

  async deactivate(): Promise<void> {
    const meterId = this.meterId();
    const typeId = this.typeId();
    if (!meterId || typeId === null || !this.firstAlarmActive()) return;

    this.$spinner.show();
    const success = await this.$alarms.deactivateAlarm(meterId, typeId);
    if (success) {
      await this.$alarms.fetchAlarmByType(typeId, this.currentPage(), true);
      this.alarmDeactivated.emit();
    }
    this.$spinner.hide();
  }
}
